import { Alert, Snackbar } from "@mui/material";
import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";

type SnackbarType = "success" | "error" | "warning";

// Only the stable dispatch function lives in context — state does not.
// This prevents consumers from re-rendering when the snackbar opens/closes.
const SnackbarContext = createContext<
  (message: string, type: SnackbarType) => void
>(() => {});

export const useSnackbar = () => {
  const displayMessage = useContext(SnackbarContext);
  return { displayMessage };
};

export const SnackbarProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [snackbar, setSnackbar] = useState<{
    message: string | null;
    severity: SnackbarType | null;
  }>({ message: null, severity: null });

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const displayMessage = useCallback((message: string, severity: SnackbarType) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setSnackbar({ message, severity });
    timeoutRef.current = setTimeout(() => {
      setSnackbar({ message: null, severity: null });
    }, 5000);
  }, []);

  const onClose = useCallback(
    (_event?: React.SyntheticEvent | Event, reason?: string) => {
      if (reason !== "clickaway") {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setSnackbar({ message: null, severity: null });
      }
    },
    [],
  );

  return (
    <SnackbarContext.Provider value={displayMessage}>
      {children}
      <Snackbar
        open={!!snackbar.message}
        onClose={onClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={onClose}
          severity={snackbar.severity || "success"}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};
